import React, { useEffect, useRef } from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CreditCard, Building2, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BankDetailsStep = () => {
  const { formData, updateFormData } = useForm();
  const lastLookedUp = useRef('');

  const formatSortCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
  };

  const handleSortCodeChange = (value) => {
    const formatted = formatSortCode(value);
    updateFormData({ sort_code: formatted });
  };

  // Auto-lookup bank name when sort code is complete (6 digits)
  useEffect(() => {
    const digits = (formData.sort_code || '').replace(/\D/g, '');
    if (digits.length === 6 && digits !== lastLookedUp.current) {
      lastLookedUp.current = digits;
      axios.get(`${API_URL}/bank-lookup/${digits}`)
        .then(res => {
          if (res.data.success && res.data.bank_name) {
            updateFormData({ bank_name: res.data.bank_name });
          }
        })
        .catch(() => {});
    }
  }, [formData.sort_code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="bank-details-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Your Financial Details
        </h2>
        <p className="text-slate-600 hidden md:block">
          Your application is looking good! For a <strong>faster decision</strong>, 
          <strong> lower rate</strong> and <strong>quicker pay-out</strong>, please fill in your bank details.
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <div className="p-4 brand-bg-light rounded-lg flex items-start gap-3">
          <CreditCard className="w-5 h-5 brand-text flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700">
            Bank details help verify your identity and speed up the application process.
            They&apos;re <strong>optional</strong> — but without them, fewer lenders may be able to make you an offer.
            We never access or debit your account from this form.
          </p>
        </div>

        <div>
          <Label htmlFor="sort_code" className="text-sm font-medium text-slate-700 mb-2 block">
            Sort Code
          </Label>
          <Input
            id="sort_code"
            placeholder="XX-XX-XX"
            value={formData.sort_code}
            onChange={(e) => handleSortCodeChange(e.target.value)}
            maxLength={8}
            className="h-12 font-mono text-lg"
            data-testid="sort-code-input"
          />
        </div>

        {/* Bank Name - auto-populated from sort code or OCR, editable */}
        <div>
          <Label htmlFor="bank_name" className="text-sm font-medium text-slate-700 mb-2 block">
            Bank Name
          </Label>
          <div className="relative">
            <Input
              id="bank_name"
              placeholder="Auto-detected from sort code"
              value={formData.bank_name}
              onChange={(e) => updateFormData({ bank_name: e.target.value })}
              className="h-12"
              data-testid="bank-name-input"
            />
            {formData.bank_name && (
              <Building2 className="absolute right-3 top-3.5 w-5 h-5 brand-text" />
            )}
          </div>
          {formData.bank_name && (
            <p className="text-xs brand-text mt-1">Auto-detected from sort code — you can edit if needed</p>
          )}
        </div>

        <div>
          <Label htmlFor="account_number" className="text-sm font-medium text-slate-700 mb-2 block">
            Account Number
          </Label>
          <Input
            id="account_number"
            placeholder="8 digit account number"
            value={formData.account_number}
            onChange={(e) => updateFormData({ account_number: e.target.value.replace(/\D/g, '').slice(0, 8) })}
            maxLength={8}
            className="h-12 font-mono text-lg"
            data-testid="account-number-input"
          />
          {/* Length warning — surfaces when OCR/paste produced a value that
              isn't a clean 8-digit UK account number. We deliberately don't
              auto-truncate beyond 8 chars (the onChange already does that for
              manual typing) — this only fires for OCR data where the backend
              has cleaned non-digits but the length is still off. */}
          {formData.account_number && formData.account_number.replace(/\D/g, '').length !== 8 && (
            <div
              className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md"
              data-testid="account-number-length-warning"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">
                UK account numbers are <strong>8 digits</strong> — you currently have <strong>{formData.account_number.replace(/\D/g, '').length}</strong>. Please double-check the number.
              </p>
            </div>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700 mb-3 block">
            Estimated time with bank
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Years</Label>
              <Select 
                value={String(formData.bank_years ?? 0)} 
                onValueChange={(value) => updateFormData({ bank_years: parseInt(value) })}
              >
                <SelectTrigger className="h-11" data-testid="bank-years-select">
                  <SelectValue>{String(formData.bank_years ?? 0)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {[...Array(51)].map((_, i) => (
                    <SelectItem key={i} value={i.toString()}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Months</Label>
              <Select 
                value={String(formData.bank_months ?? 0)} 
                onValueChange={(value) => updateFormData({ bank_months: parseInt(value) })}
              >
                <SelectTrigger className="h-11" data-testid="bank-months-select">
                  <SelectValue>{String(formData.bank_months ?? 0)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i} value={i.toString()}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankDetailsStep;
